import type { FeatureContext } from '../../feature-context';
import { setAnalyticsContext, setDefaultTraits } from '../../utils/analytics';
import { analyticsReady } from '../../utils/ready';
import { parseStructuredDataJSON } from '../../utils/structured-data';
import { logger } from './logger';

// Extend Window interface for analytics
declare global {
  interface Window {
    analytics: {
      track(
        event: string,
        properties?: Record<string, any>,
        context?: any,
        callback?: () => void
      ): void;
      identify(
        userId?: string | Record<string, any>,
        traits?: Record<string, any>,
        context?: any,
        callback?: () => void
      ): void;
      page(
        name?: string | Record<string, any>,
        properties?: Record<string, any>,
        context?: any
      ): void;
      reset(): void;
      ready(callback: () => void): void;
      on?(event: string, callback: () => void): void;
      user(): {
        id(): string | null;
        anonymousId(): string;
        traits(): Record<string, any>;
      };
      _defaultContext?: Record<string, any>;
      _defaultTraits?: { retailerName?: string };
    };
  }
}

function failsafeCall<T extends (...args: any[]) => any>(
  func: T,
  ...args: Parameters<T>
): ReturnType<T> | undefined {
  try {
    return func(...args);
  } catch (e) {
    logger.error('Could not extract page attribute', e);
    return undefined;
  }
}

interface Variables {
  timeout?: number;
  retailerName?: string;
  pageVars?: Record<string, unknown>;
  application?: string;
  skipPageview?: boolean;
}

const ACCEPTED_SCHEMA_TYPES = ['Product'];

function extractPageCategory(doc: HTMLDocument): string | undefined {
  const schemaElement = doc.querySelector<HTMLScriptElement>(
    'script[type="application/ld+json"]'
  );

  if (schemaElement == null) {
    return undefined;
  }

  const parsedSchema = parseStructuredDataJSON(schemaElement, logger);
  const schemaType = parsedSchema['@type'];

  if (
    typeof schemaType === 'string' &&
    ACCEPTED_SCHEMA_TYPES.indexOf(schemaType) >= 0
  ) {
    return parsedSchema['@type'] as string;
  }

  return undefined;
}

function extractPageName(doc: HTMLDocument): string | undefined {
  return doc.querySelector<HTMLMetaElement>('meta[itemprop=name]')?.content;
}

function setContext(variables: Variables, context: FeatureContext) {
  setDefaultTraits({
    retailerName: variables.retailerName,
  });

  setAnalyticsContext({
    groupId: context.universalRetailerId,
    app: {
      name: variables.application || 'unknown',
    },
  });
}

export async function install(
  variables: Variables,
  context: FeatureContext
): Promise<void> {
  await analyticsReady();

  setContext(variables, context);

  window.analytics!.identify(
    {
      'universal-retailer-id': context.universalRetailerId,
    },
    window.analytics._defaultContext
  );

  if (!variables.skipPageview) {
    const pageCategory = failsafeCall(extractPageCategory, document);
    const pageName = failsafeCall(extractPageName, document);

    setAnalyticsContext({ page: { name: pageName, category: pageCategory } });

    const pageAttrs = {
      ...(variables.pageVars || {}),
      name: pageName,
      category: pageCategory,
      application: variables.application,
      retailer: {
        universalRetailerId: context.universalRetailerId,
        name: variables.retailerName,
      },
    };

    window.analytics.page(pageAttrs, window.analytics._defaultContext);
  }
}
